// hi
import React from "react";
import { Routes, Route, Outlet, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { Toaster } from "sonner";
import ProtectedRoute from "./components/ProtectedRoute";
import ProtectedAdminRoute from "./components/ProtectedAdminRoute";
import ProtectedUploadRoute from "./components/ProtectedUploadRoute";
import { temporary_fix_disable_commission } from "./utils/featureFlags";
import Login from "./components/Login";
import Register from "./components/Register";
import VerifyEmail from "./components/VerifyEmail";
import VerifyEmailChange from "./components/VerifyEmailChange";
import EmailVerificationRequired from "./components/EmailVerificationRequired";
import TokenDebugger from "./components/TokenDebugger";
import AdminUserPanel from "./components/admin/users";
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";
import InviteLink from "./components/InviteLink";

// Components
import Dashboard from "./components/Dashboard";
import AffiliateView from "./components/AffiliateView";
import NewAffiliateView from "./components/new-affiliate-view";
import OldDashboard from "./components/old-dashboard";
import LoadHistoricalData from "./components/load-historical-data";
import HomePage from "./components/common/homepage";
import AppLayout from "./components/AppLayout"; // New combined layout
import AffiliateLayout from "./components/affiliate/AffiliateLayout";
import PerformancePage from "./components/affiliate/PerformancePage";
import BankAccountsPage from "./components/affiliate/BankAccountsPage";
import ReferralCenterPage from "./components/affiliate/ReferralCenterPage";
import CreativeSubmissionPage from "./components/CreativeSubmissionPage";
import PayoutPage from "./components/affiliate/PayoutPage";
import AdminSettingsPage from "./components/admin/AdminSettingsPage";
import AffiliateSettingsPage from "./components/affiliate/AffiliateSettingsPage";
import LeaderboardPage from "./components/affiliate/LeaderboardPage";
import CsvUploadPage from "./components/admin/CsvUploadPage";
import ProductsPage from "./components/affiliate/ProductsPage";
import ProductDetailPage from "./components/affiliate/ProductDetailPage";
import EducationPage from "./components/affiliate/EducationPage";
import EducationDetailPage from "./components/affiliate/EducationDetailPage";
import MediaBuyerLayout from "./components/media-buyer/MediaBuyerLayout";
import MediaBuyerCreativeSubmissions from "./components/media-buyer/MediaBuyerCreativeSubmissions";
import MediaBuyerSettings from "./components/media-buyer/MediaBuyerSettings";
import ProductManagement from "./components/admin/ProductManagement";
import EducationManagement from "./components/admin/EducationManagement";
import BrandManagement from "./components/admin/BrandManagement";
import BrandOptionsPage from "./components/admin/BrandOptionsPage";
import EditCommissionsPage from "./components/admin/EditCommissionsPage";
import SetUsernamePage from "./components/SetUsernamePage";
import LandingPage from "./components/LandingPage";
import PayoutRequesters from "./components/admin/PayoutRequesters";

// OlympusChat Components
import AffiliateChatDashboard from "./components/chat/AffiliateChatDashboard";
import MediaBuyerChatDashboard from "./components/media-buyer/MediaBuyerChatDashboard";

// Chat Cleanup Hook
import { useChatCleanup } from "./hooks/useChatCleanup";
import OAuthWrapper from "./components/GoogleSSO/OAuthWrapper";

// Main application component with routing configuration
const App = () => {
  // Initialize global chat cleanup
  const { getActiveSubscriptions } = useChatCleanup();

  // Debug: Log active subscriptions count in development
  if (process.env.NODE_ENV === "development") {
    const subscriptionCount = getActiveSubscriptions();
    if (subscriptionCount > 0) {
      console.log(`🔔 Active chat subscriptions: ${subscriptionCount}`);
    }
  }

  return (
    <AuthProvider>
      <Toaster position="top-right" richColors closeButton theme="dark" />
      <Routes>
        {/* Public Routes */}
        <Route path="/landing" element={<LandingPage />} />
        <Route path="/demo" element={<LandingPage />} />
        <Route path="/login" element={<OAuthWrapper><Login /></OAuthWrapper>} />
        <Route path="/register" element={<OAuthWrapper><Register /></OAuthWrapper>} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/verify-email-change" element={<VerifyEmailChange />} />
        <Route path="/login/verify-email" element={<VerifyEmail />} />
        <Route
          path="/verify-email-required"
          element={<EmailVerificationRequired />}
        />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/login/reset-password" element={<ResetPassword />} />
        <Route path="/invite/:affiliateName" element={<InviteLink />} />
        <Route path="/debug-tokens" element={<TokenDebugger />} />
        <Route path="/set-username" element={
          <ProtectedRoute requiresUsername={false}>
            <SetUsernamePage />
          </ProtectedRoute>
        } />

        {/* Protected Routes with Layout */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          {/* Admin-only routes */}
          <Route
            element={
              <ProtectedAdminRoute>
                <Outlet />
              </ProtectedAdminRoute>
            }
          >
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="users" element={<AdminUserPanel />} />
            <Route path="payout-requesters" element={<PayoutRequesters />} />
            <Route path="admin-settings" element={<AdminSettingsPage />} />
            <Route
              path="admin-settings/upload-csv"
              element={<CsvUploadPage />}
            />
            <Route path="products" element={<ProductManagement />} />
            <Route path="brands" element={<BrandManagement />} />
            <Route path="brands/:brandId" element={<BrandOptionsPage />} />
            <Route path="brands/:brandId/edit-commissions" element={<EditCommissionsPage />} />
            <Route
              path="education-management"
              element={<EducationManagement />}
            />
            <Route
              path="load-historical-data"
              element={<LoadHistoricalData />}
            />
            <Route path="old" element={<OldDashboard />} />
          </Route>

          {/* Upload route: admins, media buyers, and per-user can_upload grants.
              Server-side the same rule is enforced by can_upload_performance_data(). */}
          <Route element={<ProtectedUploadRoute />}>
            <Route path="upload-csv" element={<CsvUploadPage />} />
          </Route>

          {/* Affiliate-specific routes (nested in its own layout) */}
          <Route path="affiliate/:affiliateName" element={<AffiliateLayout />}>
            {/* When commission display is disabled, redirect to payout instead of showing Performance */}
            <Route index element={temporary_fix_disable_commission ? <Navigate to="payout" replace /> : <PerformancePage />} />
            <Route path="leaderboard" element={<LeaderboardPage />} />
            <Route path="payout" element={<PayoutPage />} />
            <Route path="bank-accounts" element={<BankAccountsPage />} />
            <Route path="education" element={<EducationPage />} />
            <Route
              path="education/:educationId"
              element={<EducationDetailPage />}
            />
            <Route path="referral-center" element={<ReferralCenterPage />} />
            <Route
              path="creative-submission"
              element={<CreativeSubmissionPage />}
            />
            <Route path="chat" element={<AffiliateChatDashboard />} />
            <Route path="products" element={<ProductsPage />} />
            <Route
              path="products/:productSlug"
              element={<ProductDetailPage />}
            />
            <Route path="settings" element={<AffiliateSettingsPage />} />
          </Route>

          {/* Media Buyer routes */}
          <Route path="media-buyer/:username" element={<MediaBuyerLayout />}>
            <Route index element={<MediaBuyerCreativeSubmissions />} />
            <Route path="chat" element={<MediaBuyerChatDashboard />} />
            <Route path="settings" element={<MediaBuyerSettings />} />
          </Route>

          {/* Default route for authenticated users */}
          <Route index element={<HomePage />} />
        </Route>

        {/* Fallback for old affiliate view if needed, might be deprecated */}
        <Route
          path="/affiliate-old/:affiliateName"
          element={
            <ProtectedRoute>
              <AffiliateView />
            </ProtectedRoute>
          }
        />
      </Routes>
    </AuthProvider>
  );
};

export default App;
